import React, { useEffect, useState, useContext } from 'react';

import { makeStyles } from '@mui/styles';
import { ButtonBase, Tooltip } from '@mui/material';

import { EventsContext } from '../EventsContext';
import TrplSelect from '../../../common/TrplControls/TrplSelect';
import PredicateChain from '../../../common/PredicateChain';

import Node from '../../../common/Node';
import Search from '../../../common/Search';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import { OpenInNew } from '@mui/icons-material';
import LoaderNew from '../../../common/LoaderNew';

import EventsStyles from '../EventsStyles';
import CommonStyles from '../../../common/CommonStyles';
import ActionConfirmation from '../../../common/ActionConfirmation';

import { PREDICATE_SUBSCRIBER } from '../EventsPage';

const useStyles = makeStyles({
  ...CommonStyles,
  ...EventsStyles,
});

const EventByPredicate = (props) => {
  const [editable, setEditable] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [sub, setSub] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { updateEvent, deleteEvent, openRstToEditEvent, nodesData, addNodeToNodesData, addSubscriber, removeDraftEvent } = useContext(EventsContext);

  const classes = useStyles({
    editable,
  });

  useEffect(() => {
    setSub(props.sub);

    if (props.sub.editable) setEditable(true);
  }, [props]);

  const editSub = (field, value) => {
    const subCopy = { ...sub };

    subCopy[field] = value;

    setSub(subCopy);
  };

  const addTrigger = () => {
    const subCopy = { ...sub };

    subCopy.triggers = [...subCopy.triggers, { event: 'onCreate', pidsChain: [] }];

    setSub(subCopy);
  };

  const removeSubTrigger = (triggerIndex) => {
    const subCopy = { ...sub };
    const triggersCopy = [...sub.triggers];

    triggersCopy.splice(triggerIndex, 1);

    subCopy.triggers = triggersCopy;

    setSub(subCopy);
  };

  const onSubTriggerChange = (triggerIndex, field, value) => {
    const subCopy = { ...sub };
    const triggersCopy = [...sub.triggers];

    const trigger = { ...triggersCopy[triggerIndex] };
    trigger[field] = value;

    triggersCopy[triggerIndex] = trigger;

    subCopy.triggers = triggersCopy;

    setSub(subCopy);
  };

  const eventReturnPropsValues = () => {
    const subCopy =  Object.assign({}, props.sub);

    setSub(subCopy);
  };

  const deleteJelModal = {
    contentText: 'Delete',
    contentBoldText: 'this event?',
    open: showDeleteModal,
  };

  const eventIsUnsaved = sub && !sub.id;

  const selectClasses = {
    root: `${classes.eventsSelect} ${classes.textFieldInput} ${editable ? '' : classes.noHoverOrFocus}`,
    select: `${classes.eventsSelect}`,
  };

  return (
    <React.Fragment>
      <ActionConfirmation
        modalData={deleteJelModal}
        acceptHandler={() => {
          deleteEvent(props.ancestor, sub.type, sub.id);
          setShowDeleteModal(false);
          setEditable(false);
        }}
        onClose={() => setShowDeleteModal(false)}
      />

      {sub !== null ? (
        <div className={classes.row} id={sub.draft_id || sub.id}>
          <div className={classes.rowContent}>
            <div className={classes.nodeCol}>
              <div style={{width: '20px', marginRight: '10px'}}>
                <span className={classes.subId}>{sub.id}</span>
              </div>
              {props.ValueExpression ?
                <img src="/images/events-predicate-read-only-icon.svg" alt="events icon" className={classes.mr15} /> :
                <img src="/images/events-predicate-icon.svg" alt="events icon" className={classes.mr15} />
              }

              <div className={classes.column}>
                <div style={{ marginBottom: '5px' }}>
                  {props.ancestor ?
                    <Node size="xs" isLink node={nodesData[props.ancestor]}/>
                  : 
                    <Search
                      conceptsOnly={true}
                      containerStyles={{
                        height: 24,
                      }}
                      onDropdownElementClick={({ node }) => {
                        addSubscriber(node.id, sub.type, sub.draft_id);
                        addNodeToNodesData(node);
                      }}
                    />
                  }
                </div>

                {sub.type !== PREDICATE_SUBSCRIBER ? (
                  <PredicateChain
                    rst={sub.subscriber}
                    maxSelectedRstCount={1}
                    addInnerRstCb={({ newSelected }) => {
                      editSub('subscriber', newSelected[0]);
                    }}
                    onRemoveRstCb={() => {
                      editSub('subscriber', []);
                    }}
                    ancestor={props.ancestor}
                    mode={editable && !props.ValueExpression ? 'edit' : 'show'}
                  />
                ) : null}
              </div>
            </div>

            <div className={classes.eventsCol} style={{ pointerEvents: props.ancestor && editable ? '' : 'none' }}>
              {sub.triggers.map((trigger, triggerIndex) => (
                <div className={`${classes.mb5} ${classes.alignCenter}`} key={`${sub.id}/${triggerIndex}`}>
                  {editable ? (
                    <div className={`${classes.alignCenter} ${classes.mr5}`} style={{ width: 111, flexShrink: 0 }}>
                      {!props.ValueExpression && (
                        <CloseIcon
                          className={classes.removeTriggerEventIcon}
                          onClick={() => removeSubTrigger(triggerIndex)}
                        />
                      )}

                      <TrplSelect
                        margin="none"
                        selectClasses={selectClasses}
                        disabled={props.ValueExpression}
                        value={trigger.event}
                        values={['onCreate', 'onUpdate', 'onDelete']}
                        onChange={(e) => {
                          onSubTriggerChange(triggerIndex, 'event', e.target.value);
                        }}
                      />{' '}
                    </div>
                  ) : (<div className={classes.trigger}>{trigger.event}</div>)}

                  <PredicateChain
                    rst={trigger.pidsChain}
                    ancestor={props.ancestor}
                    addInnerRstCb={({ newSelected }) => {
                      onSubTriggerChange(triggerIndex, 'pidsChain', newSelected);
                    }}
                    onRemoveRstCb={(props, newSelected) => {
                      onSubTriggerChange(triggerIndex, 'pidsChain', newSelected);
                    }}
                    mode={editable && !props.ValueExpression ? 'edit' : 'show'}
                  />
                </div>
              ))}

              {editable && !props.ValueExpression && (
                <ButtonBase
                  onClick={addTrigger}
                  className={`${classes.addTriggerBtn} ${classes.btn} ${classes.btnEmpty}`}
                >
                  <AddIcon className={classes.addTriggerBtnIcon} /> Add trigger
                </ButtonBase>
              )}
            </div>

            <div className={classes.funcCol}>
              <textarea
                disabled={!props.ancestor}
                value={sub.function}
                className={`${classes.textArea} ${editable ? '' : classes.noHoverOrFocus}`}
                onChange={(e) => {
                  if (editable) editSub('function', e.target.value);
                }}
              />
            </div>
            <div className={classes.editRowCol}>
              <div className={classes.ml20}>
                {!editable && (
                  <Tooltip
                    title="Edit"
                    placement="top"
                    classes={{
                      tooltip: classes.muiTooltip,
                    }}
                  >
                    <EditIcon
                      onClick={() => {
                        setEditable(true);
                      }}
                    />
                  </Tooltip>
                )}
                {sub.type !== PREDICATE_SUBSCRIBER ? (
                  <Tooltip
                    title='Open predicate to edit'
                    placement='top'
                    classes={{
                      tooltip: classes.muiTooltip
                    }}
                    style={{ marginTop: '20px' }}
                  >
                    <OpenInNew
                      onClick={() => {
                        openRstToEditEvent(props.ancestor, props.sub.subscriber);
                      }}
                    />
                  </Tooltip>
                ) : null}
              </div>
            </div>
          </div>

          {editable ? (
            <div className={classes.rowButtonsContainer}>
              {!eventIsUnsaved && !props.ValueExpression ? (
                <ButtonBase
                  className={`${classes.rowActionBtn} ${classes.btn} ${classes.btnEmpty} ${classes.mr10}`}
                  onClick={() => {
                    setShowDeleteModal(true);
                  }}
                >
                  <span>Delete</span>

                  <DeleteIcon className={classes.deleteRowIcon} />
                </ButtonBase>
              ) : <div />}

              <div className={classes.alignCenter}>
                <ButtonBase
                  className={`${classes.rowActionBtn} ${classes.btn} ${classes.btnEmpty} ${classes.mr10}`}
                  onClick={() => {
                    eventReturnPropsValues();
                    setEditable(false);

                    // unsaved events shouldn't be in events list after editing
                    if (eventIsUnsaved) {
                      if (props.ancestor) {
                        deleteEvent(props.ancestor, sub.type, sub.draft_id);
                      } else removeDraftEvent(sub.type);
                    }
                  }}
                >
                  Cancel
                </ButtonBase>

                <ButtonBase
                  disabled={!props.ancestor || (
                    sub.type !== PREDICATE_SUBSCRIBER && (!sub.subscriber || sub.subscriber.length === 0)
                  )}
                  classes={{
                    disabled: classes.saveBtnDisabled,
                  }}
                  className={`${classes.rowActionBtn} ${classes.btn} ${classes.btnNormal}`}
                  onClick={() => {
                    if (!sub.entityType) sub.entityType = 200;
                    setSaveLoading(true);

                    updateEvent(props.ancestor, sub.type, sub, (updatedSub) => {
                      setSub(updatedSub);

                      setEditable(false);
                      setSaveLoading(false);
                    });
                  }}
                >
                  {saveLoading ? <LoaderNew /> : 'Save'}
                </ButtonBase>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </React.Fragment>
  );
};

export default EventByPredicate;
